'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Trophy, Star, Clock, Lightbulb, Target, RotateCcw, Home } from 'lucide-react';
import { GameResult, GameDifficulty } from '@/contexts/games/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';

interface GameVictoryModalProps {
  isOpen: boolean;
  result: GameResult;
  onNewGame: () => void;
  onChangeDifficulty?: () => void;
  onIncreaseDifficulty?: () => void;
  onBackToGames?: () => void;
  onClose?: () => void;
  customStats?: React.ReactNode;
  showDifficultyButton?: boolean;
  showBackButton?: boolean;
  canIncreaseDifficulty?: boolean;
  showHints?: boolean;
}

/**
 * Универсальное модальное окно победы для игр
 */
export function GameVictoryModal({
  isOpen,
  result,
  onNewGame,
  onChangeDifficulty,
  onIncreaseDifficulty,
  onBackToGames,
  onClose,
  customStats,
  showDifficultyButton = true,
  showBackButton = false,
  canIncreaseDifficulty = false,
  showHints = true,
}: GameVictoryModalProps) {
  const t = useTranslations();

  // Определяем качество результата на основе времени и подсказок
  const getPerformanceRating = (): 'perfect' | 'excellent' | 'good' | 'okay' => {
    if (result.hintsUsed === 0 && result.time < 60000) return 'perfect'; // < 1 минуты без подсказок
    if (result.hintsUsed <= 1 && result.time < 120000) return 'excellent'; // < 2 минут с 1 подсказкой
    if (result.hintsUsed <= 2 && result.time < 300000) return 'good'; // < 5 минут с 2 подсказками
    return 'okay';
  };

  // Получаем эмодзи для сложности
  const getDifficultyEmoji = (difficulty: GameDifficulty): string => {
    const emojis = {
      easy: '🟢',
      medium: '🟡',
      hard: '🟠',
      expert: '🔴',
    };
    return emojis[difficulty] || '⚪';
  };

  // Форматируем время
  const formatTime = (timeInMs: number): string => {
    const totalSeconds = Math.floor(timeInMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const performance = getPerformanceRating();
  const timeFormatted = formatTime(result.time);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="game-victory-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-center justify-center" data-testid="game-victory-title">
            <Trophy className="h-6 w-6 text-warning-foreground dark:text-warning" />
            {t('games.victory.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Поздравительное сообщение */}
          <div className="text-center space-y-2" data-testid="game-victory-performance" data-performance={performance}>
            <div className="text-4xl" data-testid="game-victory-emoji">
              {performance === 'perfect' && '🎉'}
              {performance === 'excellent' && '⭐'}
              {performance === 'good' && '👏'}
              {performance === 'okay' && '👍'}
            </div>
            <h3 className="text-lg font-semibold" data-testid="game-victory-performance-title">
              {t(`games.victory.${performance}.title`)}
            </h3>
            <p className="text-muted-foreground" data-testid="game-victory-performance-message">
              {t(`games.victory.${performance}.message`)}
            </p>
          </div>

          {/* Статистика */}
          <div className="grid grid-cols-2 gap-4" data-testid="game-victory-stats-grid">
            {/* Время */}
            <div className="flex flex-col items-center p-3 bg-primary/10 rounded-lg" data-testid="game-victory-stat-time">
              <Clock className="h-5 w-5 text-primary mb-1" />
              <span className="text-sm text-muted-foreground">{t('games.time')}</span>
              <span className="font-mono font-semibold" data-testid="game-victory-time-value">{timeFormatted}</span>
            </div>

            {/* Сложность */}
            <div className="flex flex-col items-center p-3 bg-primary/10 rounded-lg" data-testid="game-victory-stat-difficulty">
              <Target className="h-5 w-5 text-primary mb-1" />
              <span className="text-sm text-muted-foreground">{t('games.difficulty.title')}</span>
              <Badge variant="secondary" className="gap-1" data-testid="game-victory-difficulty-value">
                {getDifficultyEmoji(result.difficulty)}
                {t(`games.difficulty.${result.difficulty}.name`)}
              </Badge>
            </div>

            {/* Подсказки */}
            {showHints && (
              <div className="flex flex-col items-center p-3 bg-success/10 rounded-lg" data-testid="game-victory-stat-hints">
                <Lightbulb className="h-5 w-5 text-success mb-1" />
                <span className="text-sm text-muted-foreground">{t('games.hintsUsed')}</span>
                <span className="font-semibold text-lg" data-testid="game-victory-hints-value">{result.hintsUsed}</span>
              </div>
            )}

            {/* Очки (если есть) */}
            {result.score !== undefined && (
              <div className="flex flex-col items-center p-3 bg-warning/10 rounded-lg" data-testid="game-victory-stat-score">
                <Star className="h-5 w-5 text-warning-foreground dark:text-warning mb-1" />
                <span className="text-sm text-muted-foreground">{t('games.score')}</span>
                <span className="font-semibold" data-testid="game-victory-score-value">{result.score}</span>
              </div>
            )}
          </div>

          {/* Кастомная статистика */}
          {customStats && (
            <div className="border-t pt-4" data-testid="game-victory-custom-stats">
              {customStats}
            </div>
          )}

          {/* Кнопки действий */}
          <div className="flex flex-col gap-2" data-testid="game-victory-actions">
            {canIncreaseDifficulty && onIncreaseDifficulty && (
              <Button
                onClick={onIncreaseDifficulty}
                className="w-full gap-2 bg-linear-to-r from-warning to-destructive hover:from-warning/90 hover:to-destructive/90"
                size="lg"
                data-testid="game-victory-try-harder-button"
              >
                <Target className="h-4 w-4" />
                {t('games.tryHarderLevel')}
              </Button>
            )}

            <Button
              onClick={onNewGame}
              className="w-full gap-2"
              size="lg"
              variant={canIncreaseDifficulty ? "outline" : "default"}
              data-testid="game-victory-play-again-button"
            >
              <RotateCcw className="h-4 w-4" />
              {t('games.playAgain')}
            </Button>

            <div className="flex gap-2">
              {showDifficultyButton && onChangeDifficulty && (
                <Button
                  variant="outline"
                  onClick={onChangeDifficulty}
                  className="flex-1 gap-2"
                  data-testid="game-victory-change-difficulty-button"
                >
                  <Target className="h-4 w-4" />
                  {t('games.changeDifficulty')}
                </Button>
              )}

              {showBackButton && onBackToGames && (
                <Button
                  variant="outline"
                  onClick={onBackToGames}
                  className="flex-1 gap-2"
                  data-testid="game-victory-back-to-games-button"
                >
                  <Home className="h-4 w-4" />
                  {t('games.backToGames')}
                </Button>
              )}
            </div>
          </div>

          {/* Мотивационное сообщение */}
          <div className="text-center text-sm text-muted-foreground" data-testid="game-victory-motivation">
            {performance === 'perfect' && t('games.victory.perfect.motivation')}
            {performance === 'excellent' && t('games.victory.excellent.motivation')}
            {performance === 'good' && t('games.victory.good.motivation')}
            {performance === 'okay' && t('games.victory.okay.motivation')}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
